import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useState, useEffect, useRef } from 'react'
import { analysisAPI, imageAPI } from '../../services/api'
import { ImageWithAnnotations, Project } from '../../types'

interface AnnotationImageViewerProps {
  projectId: string
  project: Project
  initialImageId?: string
}

// Color palette for different annotators
const ANNOTATOR_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
]

export default function AnnotationImageViewer({
  projectId,
  project,
  initialImageId,
}: AnnotationImageViewerProps) {
  const [images, setImages] = useState<any[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [imageWithAnnotations, setImageWithAnnotations] = useState<ImageWithAnnotations | null>(
    null
  )
  const [loading, setLoading] = useState(false)
  const [imageError, setImageError] = useState<string | null>(null)
  const [selectedAnnotators, setSelectedAnnotators] = useState<string[]>(['merged'])
  const [showMerged, setShowMerged] = useState(true)
  const [pointSize, setPointSize] = useState(6)
  const [lineThickness, setLineThickness] = useState(3)
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null)
  const [pixelColor, setPixelColor] = useState<string | null>(null)
  const [selectedMergedObjectIndex, setSelectedMergedObjectIndex] = useState<number>(-1) // -1 means show all
  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchImages()
  }, [projectId])

  useEffect(() => {
    if (images.length > 0) {
      fetchImageAnnotations(images[currentImageIndex].id)
    }
  }, [currentImageIndex, images])

  // Handle when initialImageId changes (e.g., user clicks on a different image in Overview)
  useEffect(() => {
    if (initialImageId && images.length > 0 && !isInitialLoad) {
      const index = images.findIndex((img: any) => img.id === initialImageId)
      if (index >= 0) {
        setCurrentImageIndex(index)
      }
    }
  }, [initialImageId, images, isInitialLoad])

  const fetchImages = async () => {
    try {
      const response = await imageAPI.listAll(projectId)
      setImages(response.data)

      // Handle initial image selection
      if (initialImageId && response.data.length > 0) {
        const index = response.data.findIndex((img: any) => img.id === initialImageId)
        if (index >= 0) {
          setCurrentImageIndex(index)
        }
      }

      setIsInitialLoad(false)
    } catch (error) {
      console.error('Error fetching images:', error)
    }
  }

  const fetchImageAnnotations = async (imageId: string) => {
    try {
      setLoading(true)
      setImageError(null)
      const response = await analysisAPI.getImageAnnotationsVisualization(projectId, imageId)
      setImageWithAnnotations(response.data)
    } catch (error) {
      console.error('Error fetching image annotations:', error)
      setImageError('Failed to load image annotations')
      setImageWithAnnotations(null)
    } finally {
      setLoading(false)
    }
  }

  const handlePrevious = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
    }
  }

  const toggleAnnotator = (annotatorId: string) => {
    if (annotatorId === 'merged') {
      setShowMerged(!showMerged)
    } else {
      setSelectedAnnotators(prev =>
        prev.includes(annotatorId) ? prev.filter(id => id !== annotatorId) : [...prev, annotatorId]
      )
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !containerRef.current) return

    const rect = imageRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    // Calculate the scale between displayed size and actual image size
    const scaleX = imageWithAnnotations?.width ? imageWithAnnotations.width / rect.width : 1
    const scaleY = imageWithAnnotations?.height ? imageWithAnnotations.height / rect.height : 1

    const actualX = Math.round(x * scaleX)
    const actualY = Math.round(y * scaleY)

    setCursorPosition({ x: actualX, y: actualY })

    // Get pixel color
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx && imageRef.current.complete) {
      canvas.width = 1
      canvas.height = 1
      ctx.drawImage(imageRef.current, x, y, 1, 1, 0, 0, 1, 1)
      const pixel = ctx.getImageData(0, 0, 1, 1).data
      setPixelColor(`rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`)
    }
  }

  const handleMouseLeave = () => {
    setCursorPosition(null)
    setPixelColor(null)
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No images available for analysis</p>
      </div>
    )
  }

  const currentImage = images[currentImageIndex]
  const annotatorMap = new Map<string, number>()
  imageWithAnnotations?.annotations.forEach(anno => {
    if (!annotatorMap.has(anno.user_id)) {
      annotatorMap.set(anno.user_id, annotatorMap.size)
    }
  })

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">Image Annotation Comparison</h3>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handlePrevious}
              disabled={currentImageIndex === 0}
              className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium">
              Image {currentImageIndex + 1} of {images.length}
            </span>
            <button
              onClick={handleNext}
              disabled={currentImageIndex === images.length - 1}
              className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
          <span className="text-sm text-gray-500">
            {currentImage.filename || `Frame ${currentImage.frame_number}`}
          </span>
        </div>

        {/* Annotator Selection */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Display Annotations:</p>
          <div className="flex flex-wrap gap-2">
            {imageWithAnnotations?.merged_annotations &&
              imageWithAnnotations.merged_annotations.length > 0 && (
                <label className="flex items-center gap-2 px-3 py-1 rounded-full border cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={showMerged}
                    onChange={() => toggleAnnotator('merged')}
                    className="rounded text-gray-600"
                  />
                  <span className="text-sm font-medium">Merged</span>
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                </label>
              )}
            {imageWithAnnotations?.annotations.map(anno => {
              const colorIndex = annotatorMap.get(anno.user_id) || 0
              return (
                <label
                  key={anno.id}
                  className="flex items-center gap-2 px-3 py-1 rounded-full border cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedAnnotators.includes(anno.user_id)}
                    onChange={() => toggleAnnotator(anno.user_id)}
                    className="rounded"
                    style={{ color: ANNOTATOR_COLORS[colorIndex % ANNOTATOR_COLORS.length] }}
                  />
                  <span className="text-sm">{anno.user_name}</span>
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: ANNOTATOR_COLORS[colorIndex % ANNOTATOR_COLORS.length],
                    }}
                  />
                </label>
              )
            })}
          </div>

          {/* Object selector for multiple merged objects */}
          {showMerged &&
            imageWithAnnotations?.merged_annotations &&
            imageWithAnnotations.merged_annotations.length > 1 && (
              <div className="mt-3">
                <label className="block text-xs text-gray-600 mb-2">
                  Merged Object:{' '}
                  {selectedMergedObjectIndex === -1
                    ? 'All'
                    : `${selectedMergedObjectIndex + 1} of ${imageWithAnnotations.merged_annotations?.length || 0}`}
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setSelectedMergedObjectIndex(-1)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      selectedMergedObjectIndex === -1
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Show All
                  </button>
                  {imageWithAnnotations.merged_annotations.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedMergedObjectIndex(idx)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        selectedMergedObjectIndex === idx
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Baby {idx + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Display Settings */}
        <div className="mt-4 border-t pt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Display Settings:</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Point Size: {pointSize}px</label>
              <input
                type="range"
                min="2"
                max="10"
                value={pointSize}
                onChange={e => setPointSize(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Line Thickness: {lineThickness}px
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={lineThickness}
                onChange={e => setLineThickness(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>

          {/* Keypoint Legend */}
          <div className="mt-4 flex items-center gap-4 text-xs text-gray-600">
            <span className="font-medium">Keypoint Types:</span>
            <div className="flex items-center gap-1">
              <svg width="16" height="16" viewBox="0 0 16 16">
                <circle cx="8" cy="8" r="5" fill="#3b82f6" />
              </svg>
              <span>Visible</span>
            </div>
            <div className="flex items-center gap-1">
              <svg width="16" height="16" viewBox="0 0 16 16">
                <rect x="3" y="3" width="10" height="10" fill="#dc2626" />
              </svg>
              <span>Inferred</span>
            </div>
          </div>
        </div>
      </div>

      {/* Image Display */}
      <div className="bg-white rounded-lg shadow p-4">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : imageError ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <p className="text-red-500 mb-2">{imageError}</p>
              <button
                onClick={() => {
                  if (images.length > 0) {
                    fetchImageAnnotations(images[currentImageIndex].id)
                  }
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        ) : imageWithAnnotations ? (
          <div
            ref={containerRef}
            className="relative"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <img
              ref={imageRef}
              src={imageWithAnnotations.image_url}
              alt={`Image ${currentImageIndex + 1}`}
              className="w-full h-auto"
              style={{ maxHeight: '600px', objectFit: 'contain' }}
              crossOrigin="anonymous"
              onError={_e => {
                console.error('Image failed to load:', imageWithAnnotations.image_url)
                setImageError('Failed to load image')
              }}
              onLoad={() => {
                setImageError(null)
              }}
            />

            {/* Cursor info overlay */}
            {cursorPosition && (
              <div className="absolute pointer-events-none" style={{ left: 0, top: 0 }}>
                <div className="bg-black/80 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span>
                      {cursorPosition.x}, {cursorPosition.y}
                    </span>
                    {pixelColor && (
                      <>
                        <div className="w-1 h-4 bg-gray-400" />
                        <div className="flex items-center gap-1">
                          <div
                            className="w-4 h-4 border border-white/50"
                            style={{ backgroundColor: pixelColor }}
                          />
                          <span>({pixelColor.match(/\d+/g)?.join(', ')})</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Overlay annotations */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${imageWithAnnotations.width} ${imageWithAnnotations.height}`}
              style={{ maxHeight: '600px' }}
            >
              {/* Draw merged annotations if selected */}
              {showMerged &&
                imageWithAnnotations.merged_annotations?.length > 0 &&
                (() => {
                  const mergedObjects = imageWithAnnotations.merged_annotations

                  // Determine which objects to show
                  const objectsToShow =
                    selectedMergedObjectIndex === -1
                      ? mergedObjects // Show all objects
                      : mergedObjects.length > 1
                        ? [mergedObjects[selectedMergedObjectIndex]] // Show selected object
                        : mergedObjects // Single object, show it

                  return objectsToShow.map(mergedObj => {
                    // Find the actual index of this object in the full array
                    const actualIdx =
                      selectedMergedObjectIndex === -1
                        ? mergedObjects.indexOf(mergedObj)
                        : selectedMergedObjectIndex

                    return (
                      <g key={`merged-obj-${actualIdx}`} opacity={0.8}>
                        {/* Draw bbox */}
                        {mergedObj.bbox.length >= 4 && (
                          <rect
                            x={mergedObj.bbox[0]}
                            y={mergedObj.bbox[1]}
                            width={mergedObj.bbox[2]}
                            height={mergedObj.bbox[3]}
                            fill="none"
                            stroke="#ef4444"
                            strokeWidth={lineThickness + 1}
                            strokeDasharray="5,5"
                          />
                        )}

                        {/* Draw keypoints */}
                        {mergedObj.keypoints.map((_, i) => {
                          if (i % 3 === 0) {
                            const x = mergedObj.keypoints[i]
                            const y = mergedObj.keypoints[i + 1]
                            const visibility = mergedObj.keypoints[i + 2]
                            if (visibility > 0) {
                              if (visibility === 1) {
                                // Draw square for inferred keypoints
                                return (
                                  <rect
                                    key={`merged-obj-${actualIdx}-kp-${i}`}
                                    x={x - pointSize}
                                    y={y - pointSize}
                                    width={pointSize * 2}
                                    height={pointSize * 2}
                                    fill="#ef4444"
                                  />
                                )
                              } else {
                                // Draw circle for visible keypoints
                                return (
                                  <circle
                                    key={`merged-obj-${actualIdx}-kp-${i}`}
                                    cx={x}
                                    cy={y}
                                    r={pointSize}
                                    fill="#ef4444"
                                  />
                                )
                              }
                            }
                          }
                          return null
                        })}
                      </g>
                    )
                  })
                })()}

              {/* Draw individual annotations */}
              {imageWithAnnotations.annotations.map(anno => {
                if (!selectedAnnotators.includes(anno.user_id)) return null
                const colorIndex = annotatorMap.get(anno.user_id) || 0
                const color = ANNOTATOR_COLORS[colorIndex % ANNOTATOR_COLORS.length]

                return (
                  <g key={anno.id} opacity={0.7}>
                    {/* Draw all bounding boxes for this annotator */}
                    {anno.annotations?.map((cocoAnn, annIdx) => (
                      <g key={`${anno.id}-ann-${annIdx}`}>
                        {/* Draw bbox */}
                        {cocoAnn.bbox && cocoAnn.bbox.length >= 4 && (
                          <rect
                            x={cocoAnn.bbox[0]}
                            y={cocoAnn.bbox[1]}
                            width={cocoAnn.bbox[2]}
                            height={cocoAnn.bbox[3]}
                            fill="none"
                            stroke={color}
                            strokeWidth={lineThickness}
                          />
                        )}

                        {/* Draw keypoints */}
                        {cocoAnn.keypoints?.map((_, i) => {
                          if (i % 3 === 0) {
                            const x = cocoAnn.keypoints![i]
                            const y = cocoAnn.keypoints![i + 1]
                            const visibility = cocoAnn.keypoints![i + 2]
                            if (visibility > 0) {
                              if (visibility === 1) {
                                // Draw square for inferred keypoints
                                return (
                                  <rect
                                    key={`${anno.id}-ann-${annIdx}-kp-${i}`}
                                    x={x - (pointSize - 1)}
                                    y={y - (pointSize - 1)}
                                    width={(pointSize - 1) * 2}
                                    height={(pointSize - 1) * 2}
                                    fill={color}
                                  />
                                )
                              } else {
                                // Draw circle for visible keypoints
                                return (
                                  <circle
                                    key={`${anno.id}-ann-${annIdx}-kp-${i}`}
                                    cx={x}
                                    cy={y}
                                    r={pointSize - 1}
                                    fill={color}
                                  />
                                )
                              }
                            }
                          }
                          return null
                        })}
                      </g>
                    ))}
                  </g>
                )
              })}

              {/* Draw skeleton for each selected annotator */}
              {project.skeleton_schema.length > 0 &&
                imageWithAnnotations.annotations.map(anno => {
                  if (!selectedAnnotators.includes(anno.user_id)) return null
                  const colorIndex = annotatorMap.get(anno.user_id) || 0
                  const color = ANNOTATOR_COLORS[colorIndex % ANNOTATOR_COLORS.length]

                  return anno.annotations?.map((cocoAnn, annIdx) => (
                    <g key={`${anno.id}-skeleton-${annIdx}`} opacity={0.5}>
                      {project.skeleton_schema.map(([start, end], index) => {
                        const kps = cocoAnn.keypoints
                        if (!kps || kps.length < Math.max(start, end) * 3) return null

                        const x1 = kps[(start - 1) * 3]
                        const y1 = kps[(start - 1) * 3 + 1]
                        const v1 = kps[(start - 1) * 3 + 2]
                        const x2 = kps[(end - 1) * 3]
                        const y2 = kps[(end - 1) * 3 + 1]
                        const v2 = kps[(end - 1) * 3 + 2]

                        if (v1 > 0 && v2 > 0) {
                          return (
                            <line
                              key={`skeleton-${index}`}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke={color}
                              strokeWidth={lineThickness}
                            />
                          )
                        }
                        return null
                      })}
                    </g>
                  ))
                })}

              {/* Draw skeleton for merged annotations if available */}
              {project.skeleton_schema.length > 0 &&
                showMerged &&
                imageWithAnnotations.merged_annotations?.length > 0 &&
                (() => {
                  const mergedObjects = imageWithAnnotations.merged_annotations

                  // Determine which objects to show
                  const objectsToShow =
                    selectedMergedObjectIndex === -1
                      ? mergedObjects // Show all objects
                      : mergedObjects.length > 1
                        ? [mergedObjects[selectedMergedObjectIndex]] // Show selected object
                        : mergedObjects // Single object, show it

                  return objectsToShow.map(mergedObj => {
                    // Find the actual index of this object in the full array
                    const actualIdx =
                      selectedMergedObjectIndex === -1
                        ? mergedObjects.indexOf(mergedObj)
                        : selectedMergedObjectIndex

                    return (
                      <g key={`skeleton-merged-obj-${actualIdx}`} opacity={0.5}>
                        {project.skeleton_schema.map(([start, end], index) => {
                          const kps = mergedObj.keypoints
                          const x1 = kps[(start - 1) * 3]
                          const y1 = kps[(start - 1) * 3 + 1]
                          const v1 = kps[(start - 1) * 3 + 2]
                          const x2 = kps[(end - 1) * 3]
                          const y2 = kps[(end - 1) * 3 + 1]
                          const v2 = kps[(end - 1) * 3 + 2]

                          if (v1 > 0 && v2 > 0) {
                            return (
                              <line
                                key={`skeleton-merged-${actualIdx}-${index}`}
                                x1={x1}
                                y1={y1}
                                x2={x2}
                                y2={y2}
                                stroke="#ef4444"
                                strokeWidth={lineThickness + 1}
                                strokeDasharray="5,5"
                              />
                            )
                          }
                          return null
                        })}
                      </g>
                    )
                  })
                })()}
            </svg>
          </div>
        ) : (
          <div className="flex items-center justify-center h-96">
            <p className="text-gray-500">No annotations available for this image</p>
          </div>
        )}
      </div>

      {/* Annotation Statistics */}
      {imageWithAnnotations && imageWithAnnotations.annotations.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Annotation Statistics</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Total Annotators</p>
              <p className="text-lg font-semibold">{imageWithAnnotations.annotations.length}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Merged Objects</p>
              <p className="text-lg font-semibold">
                {imageWithAnnotations.merged_annotations?.length || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Merge Strategy</p>
              <p className="text-lg font-semibold">
                {imageWithAnnotations.merged_annotations?.[0]?.merge_strategy || 'average'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Image Size</p>
              <p className="text-lg font-semibold">
                {imageWithAnnotations.width} × {imageWithAnnotations.height}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
